import { useMemo, useEffect, useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Settings } from '../../interfaces';
import { ArticleContent } from '../organisms/FaqPage/ArticleContent';
import { useSettings } from '../../stateManagement/SettingsContext';
import { Layout } from '../templates';
import { SearchBar } from '../organisms/SearchBar';
import { RelatedArticles } from '../organisms/FaqPage/RelatedArticles';
import { CustomText } from '../atoms';

type FaqPagePreviewProps = {
  onClickLandingPageButton: () => void;
  onClickMyTickets: () => void;
  onContactUsClick: () => void;
  onClickMore: (title: string) => void;
  settings: Settings;
  titleForFaq: string;
};
export const FaqPagePreview: React.FC<FaqPagePreviewProps> = ({
  onClickLandingPageButton,
  onClickMyTickets,
  onContactUsClick,
  onClickMore,
  settings,
  titleForFaq,
}) => {
  const { faqs } = useSettings();

  const [sectionBannerUrl, setSectionBannerUrl] = useState<string | null>(null);
  const [currentPublishId, setCurrentPublishId] = useState<string>(titleForFaq);
  const language = 'en';
  const faqToDisplay = useMemo(() => {
    return currentPublishId ? faqs?.find(faq => faq.publish_id === currentPublishId) : null;
  }, [currentPublishId, faqs]);

  const articleContent = faqToDisplay?.translations?.[language]?.body || 'No content available.';
  const articleTitle = faqToDisplay?.translations?.[language]?.title || 'No Title Available';
  const articleId = faqToDisplay?.id || 'No Id Available';
  const publishId = faqToDisplay?.publish_id || 'No Id Available';
  const sectionName = faqToDisplay?.section_translations?.[language]?.title || 'No Title Available';
  const lastUpdatedAt = faqToDisplay?.updated_at || '';
  const createdAt = faqToDisplay?.created_at || '';

  const updatedDateObj = new Date(lastUpdatedAt);
  const createdDateObj = new Date(createdAt);

  const dateFormatOptions: Intl.DateTimeFormatOptions = {
    month: 'long', // January
    day: '2-digit', // 09
    year: 'numeric', // 2025
  };

  const formattedUpdatedDate = lastUpdatedAt
    ? updatedDateObj.toLocaleDateString('en-US', dateFormatOptions)
    : '';

  const formattedCreatedDate = createdAt
    ? createdDateObj.toLocaleDateString('en-US', dateFormatOptions)
    : '';

  const notFound = !faqToDisplay || !articleContent;

  const { t }: { t: (key: string, options?: { returnObjects: boolean }) => any } = useTranslation();
  const helperTexts = t('data.helperTexts', { returnObjects: true });
  const categoryHeadings = t('data.categoryHeadings', { returnObjects: true });

  // Set banner image based on section ID
  useEffect(() => {
    const sectionId = faqToDisplay?.section_id;
    if (sectionId) {
      const cdnurl = process.env.REACT_APP_CDN_URL;
      const imageUrl = `${cdnurl}/section-images/${sectionId}.png`;

      fetch(imageUrl, { method: 'HEAD' })
        .then(res => {
          if (res.ok) {
            setSectionBannerUrl(imageUrl);
          } else {
            setSectionBannerUrl(null);
          }
        })
        .catch(() => {
          setSectionBannerUrl(null);
        });
    }
  }, [faqToDisplay]);

  function onClickRelatedArticle(publish_id: string) {
    setCurrentPublishId(publish_id);
  }

  useEffect(() => {
    if (!notFound) {
      window.scrollTo(0, 0);
    }
  }, [notFound]);

  if (notFound) {
    return (
      <Box
        px={{ base: '2vw', md: '1vw', lg: '15vw' }}
        backgroundColor={settings?.configs.primaryBgColor}
        textAlign="center"
        py={8}
      >
        <Text fontSize="2xl" color={settings?.configs.secondaryTextColor}>
          404 - Article Not Found
        </Text>
        <Text fontSize="lg" color={settings?.configs.secondaryTextColor}>
          The requested article could not be found. Please check the URL or try again later.
        </Text>
      </Box>
    );
  }

  return (
    <Layout
      onClickLandingPageButton={onClickLandingPageButton}
      onClickMyTickets={onClickMyTickets}
      onContactUsClick={onContactUsClick}
      pageBgColor={settings?.configs.primaryBgColor}
      settings={settings}
      isPreviewMode
    >
      {/* 🔹 Full-width Search Bar with Dynamic Banner Image */}
      <Box
        // width="100vw"
        height={{ base: '100px', md: '200px', lg: '300px' }}
        backgroundImage={`url(${sectionBannerUrl || settings?.configs.bannerImageUrl})`}
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        display="flex"
        alignItems="center"
        justifyContent="center"
        px={{ base: '4vw', md: '10vw', lg: '20vw' }}
      >
        <Box width="100%">
          <SearchBar
            searchBarTextColor={settings?.configs.secondaryTextColor}
            searchBarPx={{
              base: '5vw',
              md: '3vw',
              lg: '1vw',
            }}
            placeholderText={
              categoryHeadings.find(
                (item: Record<string, string>) => 'searchBarPlaceholderText' in item,
              )?.searchBarPlaceholderText || 'Search for Articles'
            }
            isPreview
          />
        </Box>
      </Box>

      {/* 🔹 Main Content Section */}
      <Box
        px={{ base: '2vw', md: '1vw', lg: '14vw' }}
        backgroundColor={settings?.configs.primaryBgColor}
        height="100%"
        py={{ base: '2vh', lg: '2vh' }}
        overflow="visible"
        borderTop="1px solid #ffffff"
      >
        <Flex mt={8} gap={8} flexDirection={{ base: 'column', lg: 'row' }}>
          <Box flex="2" shadow="md">
            <ArticleContent
              title={articleTitle}
              lastUpdated={formattedUpdatedDate}
              createdDate={formattedCreatedDate}
              content={articleContent}
              textColor={settings?.configs.secondaryTextColor}
              bgColor={settings?.configs.secondaryBgColor}
              sectionName={sectionName}
              id={articleId}
              publishId={publishId}
              feedbackText={
                helperTexts.find((item: Record<string, string>) => 'feedbackText' in item)
                  ?.feedbackText
              }
              isPreview
              onClickMore={onClickMore}
              onClickLandingPageButton={onClickLandingPageButton}
              secondaryAccentColor={settings?.configs.secondaryAccentColor}
            />
          </Box>
          <Box flex="1" backgroundColor={settings?.configs.primaryBgColor}>
            <Box mb={2}>
              <CustomText fontWeight="bold" variant="h1">
                {
                  categoryHeadings.find(
                    (item: Record<string, string>) => 'relatedArticlesHeading' in item,
                  )?.relatedArticlesHeading
                }
              </CustomText>
            </Box>
            <RelatedArticles
              bgColor={settings?.configs.secondaryBgColor}
              textColor={settings?.configs.secondaryTextColor}
              onClickRelatedArticle={publish_id => {
                onClickRelatedArticle(publish_id);
              }}
              faqId={titleForFaq}
              faqTitle={articleTitle}
              isPreview
            />
          </Box>
        </Flex>
      </Box>
    </Layout>
  );
};
